import { api } from '@/utils/api';
import { socketService } from '@/utils/socket';
import { WSEvent } from '@shared/constants';
import type { VehicleResult, WSEventPayload } from '@shared/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useResultsStore = defineStore('results', () => {
    // State
    const results = ref<VehicleResult[]>([]);
    const selectedResultIds = ref<Set<number>>(new Set());
    const isLoading = ref(false);
    const currentJobId = ref<string | null>(null);

    // Getters
    const selectedResults = computed(() =>
        results.value.filter(r => selectedResultIds.value.has(r.id))
    );

    const resultCount = computed(() => results.value.length);

    const hasSelection = computed(() => selectedResultIds.value.size > 0);

    // Actions
    const fetchResults = async (jobId?: string): Promise<void> => {
        isLoading.value = true;
        try {
            const params = jobId ? { job_id: jobId } : {};
            const response = await api.get<{ results: VehicleResult[] }>('/api/results', params);
            results.value = response.results;
            currentJobId.value = jobId || null;
        } catch (error: unknown) {
            console.error('Failed to fetch results:', error);
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const deleteResult = async (resultId: number): Promise<void> => {
        try {
            await api.delete(`/api/results/${resultId}`);
            removeResult(resultId);
        } catch (error: unknown) {
            console.error('Failed to delete result:', error);
            throw error;
        }
    };

    const bulkDeleteResults = async (ids: number[]): Promise<void> => {
        try {
            await api.post('/api/results/bulk-delete', { ids });
            ids.forEach(id => removeResult(id));
            selectedResultIds.value.clear();
        } catch (error: unknown) {
            console.error('Failed to bulk delete results:', error);
            throw error;
        }
    };

    const addResult = (result: VehicleResult): void => {
        results.value.unshift(result);
    };

    const removeResult = (resultId: number): void => {
        const index = results.value.findIndex(r => r.id === resultId);
        if (index !== -1) {
            results.value.splice(index, 1);
        }
        selectedResultIds.value.delete(resultId);
    };

    const toggleSelection = (resultId: number): void => {
        if (selectedResultIds.value.has(resultId)) {
            selectedResultIds.value.delete(resultId);
        } else {
            selectedResultIds.value.add(resultId);
        }
    };

    const selectAll = (): void => {
        results.value.forEach(r => selectedResultIds.value.add(r.id));
    };

    const clearSelection = (): void => {
        selectedResultIds.value.clear();
    };

    // WebSocket listeners
    const initializeWebSocket = (): void => {
        const socket = socketService.getSocket();
        if (!socket) {
            return;
        }

        socket.on(WSEvent.RESULT_ADDED, (payload: WSEventPayload[WSEvent.RESULT_ADDED]) => {
            // Only add result if:
            // 1. No job filter is set, OR
            // 2. The result belongs to the filtered job
            if (!currentJobId.value || payload.jobId === currentJobId.value) {
                // Instead of reloading the entire table, just add the result
                // to avoid jumping to page 1 and interrupting the user
                addResult(payload.result);
            }
        });
    };

    return {
        results,
        selectedResultIds,
        isLoading,
        loading: isLoading, // Alias for template usage
        currentJobId,
        selectedResults,
        resultCount,
        hasSelection,
        fetchResults,
        deleteResult,
        bulkDeleteResults,
        addResult,
        removeResult,
        toggleSelection,
        selectAll,
        clearSelection,
        initializeWebSocket
    };
});
